/** Shared display and market-slug formatting keeps caller conventions consistent. */
import type { useTranslations } from "next-intl";

import { NumberFormatter } from "./number-formatter";

export interface FormatNumberOptions {
  decimals?: number;
  minDecimals?: number;
  maxDecimals?: number;
}

export interface FormatNumberCompactOptions extends FormatNumberOptions {
  lowercase?: boolean;
  style?: keyof Intl.NumberFormatOptionsStyleRegistry;
}

export interface FormatTimeDurationOptions {
  days?: boolean;
  hours?: boolean;
  minutes?: boolean;
  seconds?: boolean;
}

export const formatTime = (timestamp: number) => {
  return new Date(timestamp).toLocaleString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export function formatDate(timestamp: number) {
  return new Date(timestamp).toLocaleString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

export const formatDateTime = (
  timestamp: number,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  },
) => {
  return new Date(timestamp).toLocaleString(undefined, options);
};

export const formatTimeDuration = ({
  seconds,
  display = { days: false, hours: true, minutes: true, seconds: false },
  tGeneral,
  formatNumber = String,
}: {
  seconds: number;
  display?: FormatTimeDurationOptions;
  tGeneral: ReturnType<typeof useTranslations<"general">>;
  formatNumber?: (value: number) => string;
}) => {
  const { days: showDays, hours: showHours, minutes: showMinutes, seconds: showSeconds } = display;

  const daysValue = Math.floor(seconds / 86400);
  const hoursValue = Math.floor((seconds % 86400) / 3600);
  const minutesValue = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const parts: string[] = [];
  if (showDays && daysValue > 0) {
    parts.push(`${formatNumber(daysValue)}${tGeneral("daysAbbrev")}`);
  }
  if (showHours && hoursValue > 0) {
    parts.push(`${formatNumber(hoursValue)}${tGeneral("hoursAbbrev")}`);
  }
  if (showMinutes && minutesValue > 0) {
    parts.push(`${formatNumber(minutesValue)}${tGeneral("minutesAbbrev")}`);
  }
  if (showSeconds && remainingSeconds > 0) {
    parts.push(`${formatNumber(remainingSeconds)}${tGeneral("secondsAbbrev")}`);
  }

  return parts.length > 0 ? parts.join(" ") : `${formatNumber(0)}${tGeneral("secondsAbbrev")}`;
};

// CLDR 48 (ICU 78) changed these; pinned so every runtime's ICU, older browsers included, agrees.
const CLDR_48_FRACTION_DIGITS: Readonly<Record<string, number>> = {
  COP: 0,
  HUF: 0,
  IDR: 0,
  PKR: 0,
  RSD: 2,
};

/** For amounts a provider quoted in its own currency, at that currency's own minor-unit digits. */
export const formatFiatAmount = (amount: number, currencyCode: string) => {
  const digits = CLDR_48_FRACTION_DIGITS[currencyCode.toUpperCase()];
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    ...(digits !== undefined && { minimumFractionDigits: digits, maximumFractionDigits: digits }),
  }).format(amount);
};

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const marketNameSerializer = {
  serialize: (marketName: string) => marketName.replace("/", "-"),
  deserialize: (serializedMarketName: string) => serializedMarketName.replace("-", "/"),
};

/**
 * Spot markets share the perp dash-slug scheme with a `-spot` suffix:
 * perp `BTC/USD` → `/trade/BTC-USD`, spot `APT/USDC` → `/trade/APT-USDC-spot`.
 * The suffix doubles as the product discriminator when resolving a slug, so a
 * perp/spot market-name collision can never mis-resolve.
 */
export const SPOT_MARKET_SLUG_SUFFIX = "-spot";

export function serializeMarketSlug(market: {
  market_name: string;
  asset_type?: "perp" | "spot";
}): string {
  const slug = marketNameSerializer.serialize(market.market_name);
  return market.asset_type === "spot" ? `${slug}${SPOT_MARKET_SLUG_SUFFIX}` : slug;
}

export function parseMarketSlug(slug: string): {
  marketName: string;
  assetType: "perp" | "spot";
} {
  // Strip the suffix BEFORE deserializing: deserialize replaces only the
  // first dash, so the suffix would otherwise corrupt the name.
  if (slug.endsWith(SPOT_MARKET_SLUG_SUFFIX)) {
    return {
      marketName: marketNameSerializer.deserialize(slug.slice(0, -SPOT_MARKET_SLUG_SUFFIX.length)),
      assetType: "spot",
    };
  }
  return { marketName: marketNameSerializer.deserialize(slug), assetType: "perp" };
}

export const formatDaysTillNow = (createdAt: number) => {
  return Math.floor((Date.now() - createdAt) / (1000 * 60 * 60 * 24));
};

/**
 * Format a taker/maker fee pair for display. Rates are decimals as returned by
 * the `user_fee_rates` endpoint (e.g. 0.00034 → "0.0340%").
 */
export const formatFees = (takerRate: number, makerRate: number) => {
  const takerFee = NumberFormatter.percent(takerRate);
  const makerFee = NumberFormatter.percent(makerRate);
  return `${takerFee} / ${makerFee}`;
};
